// src/modules/workspace/DashboardEngine.jsx
// ─────────────────────────────────────────────────────────────────────────────
// EXPLORE + FREE-DRAG DASHBOARD ENGINE
//
// EXPLORE tab:
//   • Auto-generated charts in a grid (masonry)
//   • Each card has drag-handle (⠿) — drag cards to reorder via HTML5 drag
//   • Card toolbar: [✎ annotate] [☆ pin to dashboard] [× hide]
//   • Custom Builder sub-tab with same toolbar
//   • Slicer panel (categorical filters)
//
// DASHBOARD tab:
//   • Free-drag canvas (mousedown→move anywhere, dot-grid bg)
//   • Each pinned card: draggable header, annotation, [↓ PNG] [✎] [× remove]
//   • Annotation modal: bold/italic/underline, font size, text color, highlight
//   • PNG download: SVG→Canvas, high-DPI 2×
//
// onDashboardUpdate(cards) — lifted to parent hub for Report access
// ─────────────────────────────────────────────────────────────────────────────
import React, {
  useState, useMemo, useRef, useCallback, useEffect,
} from 'react';
import * as echarts from 'echarts';
import {
  C, PALETTE, inferTypes, colStats, getHistogram, getValueCounts, pearson,
} from './wsShared';
import DataTable from './DataTable';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const MAX_RENDER     = 500_000;  // effectively no cap — process all rows
const MAX_SCATTER    = 1_500;
const MAX_BUBBLE     = 600;
const MAX_PIE_SLICES = 12;
const MAX_BAR_GROUPS = 30;
const MAX_TS_POINTS  = 150;
const MAX_AUTO_CARDS = 15;

// ─────────────────────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────────────────────
function smartSample(rows, max = MAX_RENDER) {
  if (rows.length <= max) return { rows, sampled: false, orig: rows.length };
  const step = Math.ceil(rows.length / max);
  const out = [];
  for (let i = 0; i < rows.length; i += step) out.push(rows[i]);
  return { rows: out.slice(0, max), sampled: true, orig: rows.length };
}
const isDateCol = (col, rows) => {
  const s = rows.slice(0, 30).map(r => r[col]).filter(Boolean);
  return s.length >= 5 && s.filter(v => !isNaN(Date.parse(v))).length > s.length * 0.7;
};
const isIdCol = (col, rows, types) => {
  const t = types.find(t => t.col === col);
  return t && t.uniqueCount / rows.length > 0.92 && rows.length > 30;
};

export function computeAggrData(rows, xCol, yCol, agg) {
  if (!xCol || !yCol) return [];
  const g = {};
  rows.forEach(r => {
    const k = String(r[xCol] || '').slice(0, 18) || '(empty)';
    if (!g[k]) g[k] = [];
    const v = parseFloat(r[yCol]);
    if (!isNaN(v)) g[k].push(v);
  });
  return Object.entries(g).map(([name, vals]) => {
    const sv = [...vals].sort((a, b) => a - b);
    const v = agg === 'sum' ? vals.reduce((a, b) => a + b, 0) : agg === 'count' ? vals.length : agg === 'min' ? sv[0] : agg === 'max' ? sv[sv.length - 1] : agg === 'median' ? sv[Math.floor(sv.length / 2)] : vals.reduce((a, b) => a + b, 0) / vals.length;
    return { name, value: +v.toFixed(4) };
  }).sort((a, b) => b.value - a.value).slice(0, MAX_BAR_GROUPS);
}

// ─────────────────────────────────────────────────────────────────────────────
// ECHARTS WRAPPER COMPONENT (exported for ReportComposer correlations)
// ─────────────────────────────────────────────────────────────────────────────
export function EChart({ option, height = 180 }) {
  const ref     = useRef(null);
  const instRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const inst = echarts.init(el, null, { renderer: 'canvas' });
    instRef.current = inst;
    const onResize = () => inst.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      inst.dispose();
      instRef.current = null;
    };
  }, []);

  // call setOption on every render — lightweight for ECharts when data is stable
  useEffect(() => {
    if (instRef.current && option) instRef.current.setOption(option, { notMerge: true });
  });

  return <div ref={ref} style={{ width: '100%', height }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART RENDERERS  (all ECharts — no Recharts)
// ─────────────────────────────────────────────────────────────────────────────
const NoData = () => (
  <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.t4, fontSize: 11, fontStyle: 'italic' }}>No renderable data</div>
);

// ── Shared axis/tooltip defaults ──────────────────────────────────────────────
const ecXCat = (data, rotate = 0) => ({
  type: 'category', data,
  axisLabel: { color: C.t4, fontSize: 8, fontFamily: 'JetBrains Mono,monospace', rotate, interval: rotate ? 0 : 'auto' },
  axisLine: { lineStyle: { color: C.b1 } }, axisTick: { show: false },
});
const ecXVal = () => ({
  type: 'value', scale: true,
  axisLabel: { color: C.t4, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' },
  axisLine: { lineStyle: { color: C.b1 } }, axisTick: { show: false }, splitLine: { show: false },
});
const ecYVal = () => ({
  type: 'value',
  axisLabel: { color: C.t4, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' },
  axisLine: { show: false }, axisTick: { show: false },
  splitLine: { lineStyle: { color: C.b0, type: 'dashed' } },
});
const ecTip = (trigger = 'axis') => ({
  trigger, backgroundColor: C.void, borderColor: C.b2,
  textStyle: { color: C.t2, fontSize: 10, fontFamily: 'JetBrains Mono,monospace' },
});
const ecGrid = (b = 32) => ({ top: 8, right: 8, bottom: b, left: 8, containLabel: true });

// ── HistChart ─────────────────────────────────────────────────────────────────
function HistChart({ rows, col, color, bins = 20, precomputedData }) {
  const stats = useMemo(() => precomputedData?.stats || colStats(rows, col), [rows, col, precomputedData]);
  const data  = useMemo(() => precomputedData?.histChart  || getHistogram(rows, col, bins), [rows, col, bins, precomputedData]);
  const c     = color || C.cyan;
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: ecTip('axis'),
    grid: ecGrid(28),
    xAxis: { ...ecXVal(), splitLine: { show: false } },
    yAxis: ecYVal(),
    series: [{
      type: 'bar', data: data.map(d => [d.label, d.count]), barWidth: '95%',
      itemStyle: { color: c, opacity: 0.85, borderRadius: [2, 2, 0, 0] },
      markLine: stats ? {
        silent: true, symbol: 'none',
        data: [{ xAxis: stats.mean, lineStyle: { color: C.gold, type: 'dashed', width: 1.5 } }],
        label: { show: false },
      } : undefined,
    }],
  }), [data, stats, c]);
  if (!data.length) return <NoData />;
  return (
    <div>
      {stats && (
        <div style={{ display: 'flex', gap: 0, marginBottom: 5, overflowX: 'auto' }}>
          {[['μ', stats.mean], ['σ', stats.std], ['med', stats.median], ['min', stats.min], ['max', stats.max]].map(([l, v]) => (
            <div key={l} style={{ padding: '2px 8px', borderRight: `1px solid ${C.b0}`, flexShrink: 0 }}>
              <div style={{ fontSize: 7, color: C.t4 }}>{l}</div>
              <div style={{ fontSize: 10, color: c, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      <EChart option={option} height={150} />
    </div>
  );
}

// ── CrossAnalysis (multi-series bar / line / area) ────────────────────────────
function CrossAnalysis({ rows, types }) {
  const numCols = types.filter(t => t.type === 'numeric').map(t => t.col);
  const catCols = types.filter(t => t.type === 'categorical' && t.uniqueCount <= 20).map(t => t.col);
  const [grpCol, setGrpCol] = useState(catCols[0] || '');
  const [view,   setView]   = useState('bar');

  useEffect(() => {
    if (!grpCol && catCols[0]) setGrpCol(catCols[0]);
  }, [catCols, grpCol]);

  const groups = useMemo(() => (
    grpCol ? [...new Set(rows.map(r => r[grpCol]).filter(Boolean))].slice(0, 8) : []
  ), [rows, grpCol]);

  const data = useMemo(() => {
    if (!grpCol) return [];
    return groups.map(g => {
      const gRows = rows.filter(r => r[grpCol] === g);
      const obj = { name: String(g).slice(0, 14) };
      numCols.slice(0, 5).forEach(nc => {
        const vals = gRows.map(r => parseFloat(r[nc])).filter(v => !isNaN(v));
        obj[nc] = vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(3) : 0;
      });
      return obj;
    });
  }, [rows, grpCol, groups, numCols]);

  const option = useMemo(() => {
    const cols = numCols.slice(0, 5);
    const isLineArea = view !== 'bar';
    return {
      backgroundColor: 'transparent',
      tooltip: ecTip('axis'),
      legend: { data: cols, bottom: 0, textStyle: { color: C.t3, fontSize: 9 } },
      grid: ecGrid(44),
      xAxis: ecXCat(data.map(d => d.name)),
      yAxis: ecYVal(),
      series: cols.map((nc, i) => ({
        type: isLineArea ? 'line' : 'bar',
        name: nc,
        data: data.map(d => d[nc]),
        smooth: view === 'area',
        symbol: isLineArea ? 'circle' : undefined,
        symbolSize: 5,
        lineStyle: isLineArea ? { color: PALETTE[i], width: 2 } : undefined,
        areaStyle: view === 'area' ? { color: PALETTE[i] + '25' } : undefined,
        itemStyle: { color: PALETTE[i], ...(!isLineArea ? { opacity: 0.8, borderRadius: [2, 2, 0, 0] } : {}) },
        barCategoryGap: '20%',
      })),
    };
  }, [data, numCols, view]);

  if (!catCols.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>Need categorical columns.</div>;
  }
  const selStyle = { background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '5px 8px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, cursor: 'pointer', outline: 'none' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: '12px 14px' }}>
        <div>
          <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.1em', marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>GROUP BY</div>
          <select value={grpCol} onChange={e => setGrpCol(e.target.value)} style={selStyle}>
            {catCols.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 3 }}>
          {['bar', 'line', 'area'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '5px 10px', border: `1px solid ${view === v ? C.cyan : C.b1}`, background: view === v ? C.cyan + '20' : 'transparent', color: view === v ? C.cyan : C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 2, cursor: 'pointer' }}>{v.toUpperCase()}</button>
          ))}
        </div>
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 16 }}>
        <div style={{ fontSize: 9, color: C.violet, letterSpacing: '.1em', fontFamily: 'JetBrains Mono,monospace', marginBottom: 10 }}>
          AVG METRICS BY {grpCol?.toUpperCase()}
        </div>
        <EChart option={option} height={280} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
        {groups.map((g, i) => {
          const gRows = rows.filter(r => r[grpCol] === g);
          return (
            <div key={g} style={{ background: C.surface, border: `1px solid ${C.b1}`, borderLeft: `3px solid ${PALETTE[i % PALETTE.length]}`, borderRadius: 3, padding: '9px 12px' }}>
              <div style={{ fontSize: 8, color: C.t4, marginBottom: 3 }}>{grpCol}: <b style={{ color: PALETTE[i % PALETTE.length] }}>{String(g).slice(0, 16)}</b></div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 16, color: C.t1 }}>{gRows.length.toLocaleString()}</div>
              <div style={{ fontSize: 9, color: C.t4 }}>rows</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── DonutChart ────────────────────────────────────────────────────────────────
function DonutChart({ rows, col, donut = true, precomputedData }) {
  const raw   = useMemo(() => precomputedData?.valueCountsChart?.slice(0, MAX_PIE_SLICES) || getValueCounts(rows, col, MAX_PIE_SLICES), [rows, col, precomputedData]);
  const total = precomputedData?.valueCountsChart ? null : rows.filter(r => r[col] != null && r[col] !== '').length;
  const shown = raw.reduce((s, d) => s + d.count, 0);
  const data  = (total !== null && shown < total) ? [...raw, { name: 'Other', count: total - shown }] : raw;
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { ...ecTip('item'), formatter: '{b}: {c} ({d}%)' },
    series: [{
      type: 'pie',
      radius: donut ? ['40%', '70%'] : ['0%', '70%'],
      center: ['50%', '50%'], padAngle: donut ? 2 : 0,
      data: data.map((d, i) => ({ name: String(d.name), value: d.count, itemStyle: { color: PALETTE[i % PALETTE.length] } })),
      label: { color: C.t3, fontSize: 8, fontFamily: 'JetBrains Mono,monospace', formatter: p => p.percent > 4 ? `${String(p.name).slice(0, 9)} ${p.percent.toFixed(0)}%` : '' },
      labelLine: { show: false },
    }],
  }), [data, donut]);
  if (!data.length) return <NoData />;
  return <EChart option={option} height={185} />;
}

// ── HBarChart ─────────────────────────────────────────────────────────────────
function HBarChart({ rows, col, precomputedData }) {
  const data = useMemo(() => precomputedData?.valueCountsChart?.slice(0, MAX_BAR_GROUPS) || getValueCounts(rows, col, MAX_BAR_GROUPS), [rows, col, precomputedData]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { ...ecTip('axis'), axisPointer: { type: 'shadow' } },
    grid: { top: 4, right: 40, bottom: 4, left: 4, containLabel: true },
    xAxis: { type: 'value', axisLabel: { color: C.t4, fontSize: 8 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: C.b0, type: 'dashed' } } },
    yAxis: { type: 'category', data: data.map(d => String(d.name).slice(0, 22)), inverse: true, axisLabel: { color: C.t2, fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }, axisLine: { lineStyle: { color: C.b1 } }, axisTick: { show: false } },
    series: [{
      type: 'bar',
      data: data.map((d, i) => ({ value: d.count, itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: [0, 3, 3, 0] } })),
      label: { show: true, position: 'right', color: C.t4, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' },
    }],
  }), [data]);
  if (!data.length) return <NoData />;
  return <EChart option={option} height={Math.min(data.length * 24 + 28, 270)} />;
}

// ── GroupedBarChart ───────────────────────────────────────────────────────────
function GroupedBarChart({ rows, catCol, numCol, cardData }) {
  const data = useMemo(() => {
    if (cardData) return cardData.map(d => ({ name: d.name, mean: +(d.value ?? d.mean ?? 0) }));
    const g = {};
    rows.forEach(r => {
      const k = String(r[catCol] || '').slice(0, 16) || '(empty)';
      if (!g[k]) g[k] = [];
      const v = parseFloat(r[numCol]);
      if (!isNaN(v)) g[k].push(v);
    });
    return Object.entries(g)
      .map(([name, vals]) => ({ name, mean: +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(3) }))
      .sort((a, b) => b.mean - a.mean).slice(0, MAX_BAR_GROUPS);
  }, [rows, catCol, numCol, cardData]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: ecTip('axis'),
    grid: ecGrid(44),
    xAxis: ecXCat(data.map(d => d.name), 25),
    yAxis: ecYVal(),
    series: [{ type: 'bar', name: `avg(${numCol})`, barCategoryGap: '18%', data: data.map((d, i) => ({ value: d.mean, itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: [3, 3, 0, 0] } })) }],
  }), [data, numCol]);
  if (!data.length) return <NoData />;
  return <EChart option={option} height={180} />;
}

// ── TimeSeriesChart ───────────────────────────────────────────────────────────
function TimeSeriesChart({ rows, dateCol, valueCol, color, cardData }) {
  const pts = useMemo(() => {
    if (cardData) return cardData;
    const raw = rows.map(r => ({ t: new Date(r[dateCol]).getTime(), v: parseFloat(r[valueCol]) }))
      .filter(p => !isNaN(p.t) && !isNaN(p.v)).sort((a, b) => a.t - b.t);
    if (!raw.length) return [];
    if (raw.length > MAX_TS_POINTS) {
      const step = Math.ceil(raw.length / MAX_TS_POINTS);
      return raw.filter((_, i) => i % step === 0).map(p => [new Date(p.t).toLocaleDateString(), p.v]);
    }
    return raw.map(p => [new Date(p.t).toLocaleDateString(), p.v]);
  }, [rows, dateCol, valueCol, cardData]);
  const c = color || C.cyan;
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: ecTip('axis'),
    grid: ecGrid(28),
    xAxis: { ...ecXCat(pts.map(p => p[0])), boundaryGap: false, axisLabel: { color: C.t4, fontSize: 8, showMinLabel: true, showMaxLabel: true, interval: Math.max(0, Math.floor(pts.length / 6) - 1) } },
    yAxis: ecYVal(),
    series: [{
      type: 'line', data: pts.map(p => p[1]), smooth: true, symbol: 'none',
      lineStyle: { color: c, width: 2 },
      areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: c + '40' }, { offset: 1, color: c + '08' }] } },
      itemStyle: { color: c },
    }],
  }), [pts, c]);
  if (!pts.length) return <NoData />;
  return <EChart option={option} height={165} />;
}

// ── ScatterChart2 ─────────────────────────────────────────────────────────────
function ScatterChart2({ rows, xCol, yCol, color }) {
  const pts = useMemo(() => {
    const step = rows.length > MAX_SCATTER ? Math.ceil(rows.length / MAX_SCATTER) : 1;
    return rows.filter((_, i) => i % step === 0).slice(0, MAX_SCATTER)
      .map(r => [parseFloat(r[xCol]), parseFloat(r[yCol])]).filter(p => !isNaN(p[0]) && !isNaN(p[1]));
  }, [rows, xCol, yCol]);
  const r = useMemo(() => pearson(rows.slice(0, 2000), xCol, yCol), [rows, xCol, yCol]);
  const c = color || C.cyan;
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { ...ecTip('item'), formatter: p => `${xCol}: ${p.value[0]}<br/>${yCol}: ${p.value[1]}` },
    grid: ecGrid(28),
    xAxis: { type: 'value', name: xCol, nameTextStyle: { color: C.t4, fontSize: 8 }, axisLabel: { color: C.t4, fontSize: 8 }, axisLine: { lineStyle: { color: C.b1 } }, axisTick: { show: false }, splitLine: { lineStyle: { color: C.b0, type: 'dashed' } } },
    yAxis: { type: 'value', name: yCol, nameTextStyle: { color: C.t4, fontSize: 8 }, axisLabel: { color: C.t4, fontSize: 8 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: C.b0, type: 'dashed' } } },
    series: [{ type: 'scatter', data: pts, symbolSize: 4, itemStyle: { color: c, opacity: 0.5 } }],
  }), [pts, c, xCol, yCol]);
  if (!pts.length) return <NoData />;
  return (
    <>
      <div style={{ fontSize: 9, color: C.t4, marginBottom: 4, fontFamily: 'JetBrains Mono,monospace' }}>
        r = <b style={{ color: Math.abs(r) > 0.5 ? C.green : C.t2 }}>{r.toFixed(3)}</b>
        {rows.length > MAX_SCATTER && <span style={{ marginLeft: 8, color: C.gold, fontSize: 8 }}>⚡ sampled</span>}
      </div>
      <EChart option={option} height={160} />
    </>
  );
}

// ── BubbleChart ───────────────────────────────────────────────────────────────
function BubbleChart({ rows, xCol, yCol, sizeCol }) {
  const data = useMemo(() => {
    const step = rows.length > MAX_BUBBLE ? Math.ceil(rows.length / MAX_BUBBLE) : 1;
    const samp = rows.filter((_, i) => i % step === 0).slice(0, MAX_BUBBLE);
    const sVals = samp.map(r => parseFloat(r[sizeCol])).filter(v => !isNaN(v));
    const mn = Math.min(...sVals) || 0, mx = Math.max(...sVals) || 1;
    return samp.map((r, i) => {
      const x = parseFloat(r[xCol]), y = parseFloat(r[yCol]), sz = parseFloat(r[sizeCol]);
      if (isNaN(x) || isNaN(y)) return null;
      const size = Math.max(3, ((isNaN(sz) ? 0.5 : (sz - mn) / ((mx - mn) || 1))) * 26 + 4);
      return { value: [x, y, size], itemStyle: { color: PALETTE[i % PALETTE.length], opacity: 0.5 } };
    }).filter(Boolean);
  }, [rows, xCol, yCol, sizeCol]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { ...ecTip('item'), formatter: p => `${xCol}: ${p.value[0]}<br/>${yCol}: ${p.value[1]}` },
    grid: ecGrid(28),
    xAxis: { type: 'value', name: xCol, nameTextStyle: { color: C.t4, fontSize: 8 }, axisLabel: { color: C.t4, fontSize: 8 }, axisLine: { lineStyle: { color: C.b1 } }, axisTick: { show: false }, splitLine: { lineStyle: { color: C.b0, type: 'dashed' } } },
    yAxis: { type: 'value', name: yCol, nameTextStyle: { color: C.t4, fontSize: 8 }, axisLabel: { color: C.t4, fontSize: 8 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: C.b0, type: 'dashed' } } },
    series: [{ type: 'scatter', data, symbolSize: d => d[2] }],
  }), [data, xCol, yCol]);
  if (!data.length) return <NoData />;
  return <EChart option={option} height={180} />;
}

// ── RadialChart ───────────────────────────────────────────────────────────────
function RadialChart({ rows, col, precomputedData }) {
  const data = useMemo(() => precomputedData?.valueCountsChart?.slice(0, 12) || getValueCounts(rows, col, 12), [rows, col, precomputedData]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { ...ecTip('item'), formatter: '{b}: {c}' },
    radar: {
      indicator: data.map(d => ({ name: String(d.name).slice(0, 12), max: Math.max(...data.map(x => x.count)) * 1.2 || 1 })),
      center: ['50%', '50%'], radius: '68%',
      axisName: { color: C.t3, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' },
      splitLine: { lineStyle: { color: C.b1 } }, splitArea: { show: false }, axisLine: { lineStyle: { color: C.b1 } },
    },
    series: [{
      type: 'radar',
      data: [{ value: data.map(d => d.count), itemStyle: { color: C.cyan }, lineStyle: { color: C.cyan, width: 2 }, areaStyle: { color: C.cyan + '28' } }],
    }],
  }), [data]);
  if (!data.length) return <NoData />;
  return <EChart option={option} height={180} />;
}

// ── NumOverviewChart ──────────────────────────────────────────────────────────
function NumOverviewChart({ rows, cols, precomputedDataMap }) {
  const data = useMemo(() => cols.slice(0, 14).map(col => {
    const s = precomputedDataMap?.[col]?.stats || colStats(rows, col);
    return s ? { name: col.slice(0, 12), mean: s.mean, max: s.max } : null;
  }).filter(Boolean), [rows, cols, precomputedDataMap]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: ecTip('axis'),
    legend: { data: ['mean', 'max'], right: 8, top: 4, textStyle: { color: C.t3, fontSize: 8 } },
    grid: { top: 24, right: 8, bottom: 44, left: 8, containLabel: true },
    xAxis: ecXCat(data.map(d => d.name), 22),
    yAxis: ecYVal(),
    series: [
      { type: 'bar', name: 'mean', barCategoryGap: '22%', data: data.map(d => d.mean), itemStyle: { color: C.cyan, opacity: 0.8, borderRadius: [3, 3, 0, 0] } },
      { type: 'bar', name: 'max', data: data.map(d => d.max), itemStyle: { color: C.violet, opacity: 0.45, borderRadius: [3, 3, 0, 0] } },
    ],
  }), [data]);
  if (!data.length) return <NoData />;
  return <EChart option={option} height={185} />;
}

// ── Aggregate helpers for RenderChart bar / line / area ───────────────────────
function AggrBarChart({ chart, rows, colorMap, cardData }) {
  const aggrData = useMemo(() => cardData || computeAggrData(rows, chart.xCol, chart.yCol, chart.agg || 'mean'), [rows, chart.xCol, chart.yCol, chart.agg, cardData]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent', tooltip: ecTip('axis'),
    grid: ecGrid(44),
    xAxis: ecXCat(aggrData.map(d => d.name), 25),
    yAxis: ecYVal(),
    series: [{ type: 'bar', name: `${chart.agg || 'mean'}(${chart.yCol})`, data: aggrData.map((d, i) => ({ value: d.value, itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: [3, 3, 0, 0] } })) }],
  }), [aggrData, chart.agg, chart.yCol]);
  if (!aggrData.length) return <NoData />;
  return <EChart option={option} height={180} />;
}

function AggrLineChart({ chart, rows, colorMap, cardData }) {
  const color = colorMap?.[chart.col || chart.xCol] || C.cyan;
  const aggrData = useMemo(() => cardData || computeAggrData(rows, chart.xCol, chart.yCol, chart.agg || 'mean'), [rows, chart.xCol, chart.yCol, chart.agg, cardData]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent', tooltip: ecTip('axis'),
    grid: ecGrid(32),
    xAxis: { ...ecXCat(aggrData.map(d => d.name)), boundaryGap: false },
    yAxis: ecYVal(),
    series: [{ type: 'line', name: chart.yCol, data: aggrData.map(d => d.value), smooth: false, symbol: 'circle', symbolSize: 5, lineStyle: { color, width: 2 }, itemStyle: { color } }],
  }), [aggrData, color, chart.yCol]);
  if (!aggrData.length) return <NoData />;
  return <EChart option={option} height={180} />;
}

function AggrAreaChart({ chart, rows, colorMap, cardData }) {
  const color = colorMap?.[chart.col || chart.xCol] || C.violet;
  const aggrData = useMemo(() => cardData || computeAggrData(rows, chart.xCol, chart.yCol, chart.agg || 'mean'), [rows, chart.xCol, chart.yCol, chart.agg, cardData]);
  const option = useMemo(() => ({
    backgroundColor: 'transparent', tooltip: ecTip('axis'),
    grid: ecGrid(32),
    xAxis: { ...ecXCat(aggrData.map(d => d.name)), boundaryGap: false },
    yAxis: ecYVal(),
    series: [{ type: 'line', name: chart.yCol, data: aggrData.map(d => d.value), smooth: true, symbol: 'none', lineStyle: { color, width: 2 }, areaStyle: { color: color + '28' }, itemStyle: { color } }],
  }), [aggrData, color, chart.yCol]);
  if (!aggrData.length) return <NoData />;
  return <EChart option={option} height={180} />;
}

// ── BoxPlot (single column) ───────────────────────────────────────────────────
function BoxPlotChart({ rows, col, color = C.cyan, cardData }) {
  const option = useMemo(() => {
    let wLo, q1, med, q3, wHi, outliers;
    if (cardData) {
      ({ box: [wLo, q1, med, q3, wHi], outliers = [] } = cardData);
    } else {
      const vals = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v)).sort((a, b) => a - b);
      if (vals.length < 5) return null;
      const n = vals.length;
      q1 = vals[Math.floor(n * 0.25)];
      med = vals[Math.floor(n * 0.5)];
      q3 = vals[Math.floor(n * 0.75)];
      const iqr = q3 - q1;
      const lo = q1 - 1.5 * iqr, hi = q3 + 1.5 * iqr;
      wLo = vals.find(v => v >= lo) ?? vals[0];
      wHi = [...vals].reverse().find(v => v <= hi) ?? vals[n - 1];
      outliers = vals.filter(v => v < lo || v > hi).slice(0, 100);
    }
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        textStyle: { fontFamily: 'JetBrains Mono,monospace', fontSize: 10 },
        backgroundColor: C.b0 + 'ee', borderColor: C.b2,
        formatter: p => p.seriesType === 'boxplot'
          ? `Min: ${wLo.toFixed(2)}<br/>Q1: ${q1.toFixed(2)}<br/>Median: ${med.toFixed(2)}<br/>Q3: ${q3.toFixed(2)}<br/>Max: ${wHi.toFixed(2)}`
          : `Outlier: ${(+p.value[1]).toFixed(2)}`,
      },
      grid: { left: 52, right: 14, top: 12, bottom: 28 },
      xAxis: { type: 'category', data: [col], axisLabel: { color: C.t4, fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }, axisLine: { lineStyle: { color: C.b1 } }, axisTick: { show: false } },
      yAxis: { type: 'value', scale: true, axisLabel: { color: C.t4, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' }, axisLine: { lineStyle: { color: C.b1 } }, splitLine: { lineStyle: { color: C.b1 + '88' } } },
      series: [
        { type: 'boxplot', data: [[wLo, q1, med, q3, wHi]], boxWidth: [30, 80], itemStyle: { color: color + '33', borderColor: color, borderWidth: 1.5 } },
        ...(outliers.length ? [{ type: 'scatter', data: outliers.map(v => [col, v]), symbolSize: 4, itemStyle: { color: C.red + 'bb' } }] : []),
      ],
    };
  }, [rows, col, color, cardData]);
  if (!option) return <NoData />;
  return <EChart option={option} height={180} />;
}

// ── Multi-column BoxPlot ──────────────────────────────────────────────────────
function MultiBoxPlotChart({ rows, cols, cardData }) {
  const option = useMemo(() => {
    const results = cardData || cols.map(col => {
      const vals = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v)).sort((a, b) => a - b);
      if (vals.length < 5) return null;
      const n = vals.length;
      const q1 = vals[Math.floor(n * 0.25)], med = vals[Math.floor(n * 0.5)], q3 = vals[Math.floor(n * 0.75)];
      const iqr = q3 - q1;
      const wLo = vals.find(v => v >= q1 - 1.5 * iqr) ?? vals[0];
      const wHi = [...vals].reverse().find(v => v <= q3 + 1.5 * iqr) ?? vals[n - 1];
      return { col, box: [wLo, q1, med, q3, wHi] };
    }).filter(Boolean);
    if (!results.length) return null;
    const rotate = results.length > 4 ? 22 : 0;
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', textStyle: { fontFamily: 'JetBrains Mono,monospace', fontSize: 10 }, backgroundColor: C.b0 + 'ee', borderColor: C.b2 },
      grid: { left: 52, right: 14, top: 12, bottom: rotate ? 50 : 30 },
      xAxis: { type: 'category', data: results.map(r => r.col), axisLabel: { color: C.t4, fontSize: 8, fontFamily: 'JetBrains Mono,monospace', rotate, interval: 0 }, axisLine: { lineStyle: { color: C.b1 } }, axisTick: { show: false } },
      yAxis: { type: 'value', scale: true, axisLabel: { color: C.t4, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' }, axisLine: { lineStyle: { color: C.b1 } }, splitLine: { lineStyle: { color: C.b1 + '88' } } },
      series: [{ type: 'boxplot', data: results.map(r => r.box), boxWidth: [20, 60], itemStyle: { color: C.cyan + '33', borderColor: C.cyan, borderWidth: 1.5 } }],
    };
  }, [rows, cols, cardData]);
  if (!option) return <NoData />;
  return <EChart option={option} height={185} />;
}

// ── Treemap (categorical) ─────────────────────────────────────────────────────
function TreemapChart({ rows, col, cardData }) {
  const option = useMemo(() => {
    const counts = cardData || getValueCounts(rows, col, 30);
    if (!counts.length) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: p => `${p.name}: ${p.value}`, textStyle: { fontFamily: 'JetBrains Mono,monospace', fontSize: 10 }, backgroundColor: C.b0 + 'ee', borderColor: C.b2 },
      series: [{
        type: 'treemap', roam: false, breadcrumb: { show: false },
        width: '100%', height: '90%', left: 0, top: 4,
        label: { show: true, fontSize: 9, fontFamily: 'JetBrains Mono,monospace', color: C.void, overflow: 'truncate' },
        itemStyle: { borderWidth: 1, borderColor: C.b0, gapWidth: 2 },
        data: counts.map((d, i) => ({ name: d.name, value: d.count, itemStyle: { color: PALETTE[i % PALETTE.length] } })),
      }],
    };
  }, [rows, col, cardData]);
  if (!option) return <NoData />;
  return <EChart option={option} height={195} />;
}

// ── Funnel (top categories) ───────────────────────────────────────────────────
function FunnelChart({ rows, col, cardData }) {
  const option = useMemo(() => {
    const counts = cardData ? cardData.slice(0, 8) : getValueCounts(rows, col, 8);
    if (!counts.length) return null;
    const sorted = [...counts].sort((a, b) => b.count - a.count);
    const total = cardData ? cardData.reduce((s, d) => s + d.count, 0) : rows.length;
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: p => `${p.name}: ${p.value} (${((p.value / total) * 100).toFixed(1)}%)`, textStyle: { fontFamily: 'JetBrains Mono,monospace', fontSize: 10 }, backgroundColor: C.b0 + 'ee', borderColor: C.b2 },
      series: [{
        type: 'funnel', sort: 'descending', gap: 1,
        left: '8%', width: '84%', top: 8, bottom: 8,
        label: { show: true, position: 'inside', fontSize: 9, fontFamily: 'JetBrains Mono,monospace', color: C.void },
        itemStyle: { borderWidth: 0 },
        data: sorted.map((d, i) => ({ name: d.name, value: d.count, itemStyle: { color: PALETTE[i % PALETTE.length] } })),
      }],
    };
  }, [rows, col, cardData]);
  if (!option) return <NoData />;
  return <EChart option={option} height={195} />;
}

// ── Parallel Coordinates (multi-numeric) ─────────────────────────────────────
function ParallelChart({ rows, cols, cardData }) {
  const option = useMemo(() => {
    const validCols = cols.filter(c =>
      cardData ? cardData.some(r => r[cols.indexOf(c)] != null) : rows.some(r => !isNaN(parseFloat(r[c])))
    ).slice(0, 8);
    if (validCols.length < 3) return null;
    const sample = cardData || (() => {
      const step = rows.length > 400 ? Math.ceil(rows.length / 400) : 1;
      return rows.filter((_, i) => i % step === 0).slice(0, 400);
    })();
    const axisExtents = validCols.map((col, ci) => {
      const vals = cardData
        ? sample.map(r => r[ci]).filter(v => v != null)
        : sample.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
      if (!vals.length) return [0, 1];
      const mn = Math.min(...vals), mx = Math.max(...vals), pad = (mx - mn) * 0.05 || 1;
      return [mn - pad, mx + pad];
    });
    return {
      backgroundColor: 'transparent',
      parallelAxis: validCols.map((col, i) => ({
        dim: i, name: col.length > 10 ? col.slice(0, 9) + '…' : col,
        min: axisExtents[i][0], max: axisExtents[i][1],
        nameTextStyle: { color: C.t3, fontSize: 8, fontFamily: 'JetBrains Mono,monospace' },
        axisLabel: { color: C.t4, fontSize: 7, fontFamily: 'JetBrains Mono,monospace' },
        axisLine: { lineStyle: { color: C.b2 } }, axisTick: { lineStyle: { color: C.b2 } },
      })),
      parallel: { left: 20, right: 20, top: 30, bottom: 10 },
      tooltip: { show: false },
      series: [{
        type: 'parallel',
        data: cardData
          ? sample.filter(r => r.every(v => v != null))
          : sample.map(r => validCols.map(c => { const v = parseFloat(r[c]); return isNaN(v) ? null : v; })).filter(d => d.every(v => v !== null)),
        lineStyle: { width: 0.7, opacity: 0.28, color: C.cyan },
        smooth: false,
      }],
    };
  }, [rows, cols, cardData]);
  if (!option) return <NoData />;
  return <EChart option={option} height={225} />;
}

// ── RenderChart dispatcher ────────────────────────────────────────────────────
export function RenderChart({ chart, rows, colorMap, precomputedData, cardData }) {
  const col   = chart.col || chart.xCol;
  const color = colorMap?.[col] || C.cyan;
  const colPd = precomputedData?.[col];
  switch (chart.type) {
    case 'num_overview': return <NumOverviewChart rows={rows} cols={chart.cols} precomputedDataMap={precomputedData} />;
    case 'multibox':     return <MultiBoxPlotChart rows={rows} cols={chart.cols} cardData={cardData} />;
    case 'parallel':     return <ParallelChart rows={rows} cols={chart.cols} cardData={cardData} />;
    case 'pie':          return <DonutChart rows={rows} col={chart.col} donut={false} precomputedData={colPd} />;
    case 'donut':        return <DonutChart rows={rows} col={chart.col} donut precomputedData={colPd} />;
    case 'hbar':         return <HBarChart rows={rows} col={chart.col} precomputedData={colPd} />;
    case 'radial':       return <RadialChart rows={rows} col={chart.col} precomputedData={colPd} />;
    case 'treemap':      return <TreemapChart rows={rows} col={chart.col} cardData={cardData} />;
    case 'funnel':       return <FunnelChart rows={rows} col={chart.col} cardData={cardData} />;
    case 'hist':         return <HistChart rows={rows} col={chart.col} color={color} precomputedData={colPd} />;
    case 'boxplot':      return <BoxPlotChart rows={rows} col={chart.col} color={color} cardData={cardData} />;
    case 'groupby':      return <GroupedBarChart rows={rows} catCol={chart.catCol} numCol={chart.numCol} cardData={cardData} />;
    case 'timeseries':   return <TimeSeriesChart rows={rows} dateCol={chart.dateCol} valueCol={chart.valueCol} color={color} cardData={cardData} />;
    case 'scatter':      return <ScatterChart2 rows={rows} xCol={chart.xCol} yCol={chart.yCol} color={color} />;
    case 'bubble':       return <BubbleChart rows={rows} xCol={chart.xCol} yCol={chart.yCol} sizeCol={chart.sizeCol} />;
    case 'bar':          return <AggrBarChart chart={chart} rows={rows} colorMap={colorMap} cardData={cardData} />;
    case 'line':         return <AggrLineChart chart={chart} rows={rows} colorMap={colorMap} cardData={cardData} />;
    case 'area':         return <AggrAreaChart chart={chart} rows={rows} colorMap={colorMap} cardData={cardData} />;
    default:             return <NoData />;
  }
}










// ─────────────────────────────────────────────────────────────────────────────
// CHART TYPE METADATA  (for "Convert to" dropdown)
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_LABELS = {
  hist: 'Histogram', boxplot: 'Box Plot', donut: 'Donut', pie: 'Pie',
  hbar: 'H. Bar', treemap: 'Treemap', funnel: 'Funnel', radial: 'Radial',
  groupby: 'Grouped Bar', bar: 'Bar', line: 'Line', area: 'Area',
  timeseries: 'Time Series', scatter: 'Scatter', bubble: 'Bubble',
  num_overview: 'Overview Bar', multibox: 'Multi BoxPlot', parallel: 'Parallel',
};

function getAlternates(chartType) {
  const map = {
    hist:         ['boxplot', 'area'],
    boxplot:      ['hist', 'area'],
    donut:        ['pie', 'treemap', 'funnel', 'hbar', 'radial'],
    pie:          ['donut', 'treemap', 'funnel', 'hbar'],
    hbar:         ['donut', 'treemap', 'funnel', 'radial'],
    treemap:      ['hbar', 'donut', 'funnel', 'radial'],
    funnel:       ['hbar', 'donut', 'treemap'],
    radial:       ['hbar', 'treemap', 'donut'],
    groupby:      ['bar', 'line', 'area'],
    bar:          ['groupby', 'line', 'area'],
    line:         ['bar', 'area', 'groupby'],
    area:         ['line', 'bar', 'groupby'],
    timeseries:   ['bar', 'line', 'area'],
    scatter:      ['bubble'],
    bubble:       ['scatter'],
    num_overview: ['multibox', 'parallel'],
    multibox:     ['num_overview', 'parallel'],
    parallel:     ['num_overview', 'multibox'],
  };
  return map[chartType] || [];
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTO CHART BUILDER  (smart selection + diversification)
// ─────────────────────────────────────────────────────────────────────────────
export function buildAutoCharts(rows, headers, types) {
  const numCols = types.filter(t => t.type === 'numeric').map(t => t.col);
  const catCols = types.filter(t => t.type === 'categorical').map(t => t.col);
  const dateCols = headers.filter(h => isDateCol(h, rows));
  const idCols   = headers.filter(h => isIdCol(h, rows, types));
  const safeNum  = numCols.filter(c => !idCols.includes(c));
  const safeCat  = catCols.filter(c => !idCols.includes(c));
  const charts = []; let id = 0;

  // Diversification: track usage count per chart type
  const typeUsed = new Map();
  const useType = t => { typeUsed.set(t, (typeUsed.get(t) || 0) + 1); return t; };
  // Pick the least-used type among candidates (tie-breaks by preference order)
  const pick = candidates => {
    let best = candidates[0], bestCnt = typeUsed.get(best) ?? 0;
    for (const t of candidates) {
      const cnt = typeUsed.get(t) ?? 0;
      if (cnt < bestCnt) { best = t; bestCnt = cnt; }
    }
    return useType(best);
  };

  // ── Multi-numeric overview (≥ 3 cols) ──────────────────────────────────────
  if (safeNum.length >= 3) {
    const type = pick(['num_overview', 'multibox', 'parallel']);
    charts.push({ id: id++, type, cols: safeNum.slice(0, 8), title: 'Numeric Columns Overview', subtitle: `${safeNum.length} columns` });
  }

  // ── Categorical proportions ────────────────────────────────────────────────
  safeCat.slice(0, 6).forEach(col => {
    const u = types.find(t => t.col === col)?.uniqueCount || 0;
    let candidates;
    if (u <= MAX_PIE_SLICES)  candidates = ['donut', 'treemap', 'funnel', 'hbar', 'pie', 'radial'];
    else if (u <= 40)         candidates = ['hbar', 'treemap', 'funnel', 'radial'];
    else                      candidates = ['radial', 'treemap', 'hbar'];
    const type = pick(candidates);
    charts.push({ id: id++, type, col, title: col, subtitle: `${u} categories` });
  });

  // ── Numeric distributions ──────────────────────────────────────────────────
  safeNum.slice(0, 6).forEach(col => {
    const type = pick(['hist', 'boxplot']);
    charts.push({ id: id++, type, col, title: `${col} — Distribution`, subtitle: type === 'hist' ? 'histogram' : 'box plot' });
  });

  // ── Categorical × Numeric group means ─────────────────────────────────────
  safeCat
    .filter(c => { const u = types.find(t => t.col === c)?.uniqueCount || 0; return u >= 2 && u <= 20; })
    .slice(0, 2)
    .forEach(cat =>
      safeNum.slice(0, 2).forEach(num => {
        const type = pick(['groupby', 'bar', 'line', 'area']);
        charts.push({ id: id++, type, catCol: cat, numCol: num, xCol: cat, yCol: num, col: cat, title: `${cat} × avg(${num})`, subtitle: 'group mean' });
      }));

  // ── Time series ────────────────────────────────────────────────────────────
  dateCols.forEach(dc =>
    safeNum.slice(0, 2).forEach(nc =>
      charts.push({ id: id++, type: 'timeseries', dateCol: dc, valueCol: nc, xCol: dc, yCol: nc, col: nc, title: `${nc} over time`, subtitle: dc })));

  // ── Scatter / Bubble for correlated numeric pairs ──────────────────────────
  if (safeNum.length >= 2) {
    const pairs = [];
    for (let i = 0; i < Math.min(safeNum.length, 6); i++)
      for (let j = i + 1; j < Math.min(safeNum.length, 6); j++)
        pairs.push({ a: safeNum[i], b: safeNum[j], r: Math.abs(pearson(rows.slice(0, 500), safeNum[i], safeNum[j])) });
    pairs.sort((a, b) => b.r - a.r).slice(0, 3).forEach(p => {
      const type = pick(['scatter', 'bubble']);
      const sizeCol = type === 'bubble' && safeNum.length >= 3 ? (safeNum.find(c => c !== p.a && c !== p.b) || safeNum[2]) : undefined;
      charts.push({ id: id++, type, xCol: p.a, yCol: p.b, col: p.a, ...(sizeCol ? { sizeCol } : {}), title: `${p.a} vs ${p.b}`, subtitle: `r≈${p.r.toFixed(2)}` });
    });
  }

  return charts.slice(0, MAX_AUTO_CARDS);
}

// ─────────────────────────────────────────────────────────────────────────────
// ANNOTATION MODAL
// ─────────────────────────────────────────────────────────────────────────────
export function AnnotationModal({ initial, onSave, onClose }) {
  const [text, setText] = useState(initial?.text || '');
  const [fmt,  setFmt]  = useState(initial?.fmt  || { bold: false, italic: false, underline: false, color: C.t1, highlight: 'transparent', size: 12, align: 'left' });
  const tog = key => setFmt(f => ({ ...f, [key]: !f[key] }));
  const COLORS = [C.t1, C.cyan, C.green, C.gold, C.red, C.violet, C.orange, C.pink, '#fff'];
  const HILIGHTS = ['transparent', C.cyan + '40', C.green + '40', C.gold + '40', C.red + '40', C.violet + '40'];

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(2,5,8,.88)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.b2}`, borderRadius: 6, width: 520, maxWidth: '96vw', overflow: 'hidden', boxShadow: '0 28px 70px rgba(0,0,0,.8)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
          <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: C.cyan, letterSpacing: '.12em', fontWeight: 700 }}>✎ CHART ANNOTATION</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.t3, cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        {/* Toolbar */}
        <div style={{ display: 'flex', gap: 4, padding: '8px 12px', background: C.raised, borderBottom: `1px solid ${C.b0}`, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* B / I / U */}
          {[['B', 'bold', { fontWeight: 700 }], ['I', 'italic', { fontStyle: 'italic' }], ['U', 'underline', { textDecoration: 'underline' }]].map(([l, k, s]) => (
            <button key={k} onClick={() => tog(k)} style={{
              width: 26, height: 26, border: `1px solid ${fmt[k] ? C.cyan : C.b1}`,
              background: fmt[k] ? C.cyan + '28' : 'transparent', color: fmt[k] ? C.cyan : C.t3,
              fontFamily: 'Georgia,serif', fontSize: 13, borderRadius: 2, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', ...s,
            }}>{l}</button>
          ))}
          <div style={{ width: 1, height: 18, background: C.b1 }} />
          {/* Font size */}
          <select value={fmt.size} onChange={e => setFmt(f => ({ ...f, size: +e.target.value }))}
            style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '3px 5px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 2, cursor: 'pointer', outline: 'none' }}>
            {[10, 11, 12, 13, 14, 16, 18, 20, 24].map(s => <option key={s} value={s}>{s}px</option>)}
          </select>
          {/* Align */}
          {[['←', 'left'], ['⊟', 'center'], ['→', 'right']].map(([l, v]) => (
            <button key={v} onClick={() => setFmt(f => ({ ...f, align: v }))} style={{
              width: 26, height: 26, border: `1px solid ${fmt.align === v ? C.cyan : C.b1}`,
              background: fmt.align === v ? C.cyan + '28' : 'transparent', color: fmt.align === v ? C.cyan : C.t3,
              fontSize: 12, borderRadius: 2, cursor: 'pointer',
            }}>{l}</button>
          ))}
          <div style={{ width: 1, height: 18, background: C.b1 }} />
          {/* Text color */}
          <span style={{ fontSize: 7, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>TXT</span>
          {COLORS.map(col => (
            <div key={col} onClick={() => setFmt(f => ({ ...f, color: col }))} style={{
              width: 14, height: 14, borderRadius: '50%', background: col, cursor: 'pointer',
              border: fmt.color === col ? '2px solid #fff' : `1px solid ${C.b2}`, flexShrink: 0,
            }} />
          ))}
          <div style={{ width: 1, height: 18, background: C.b1 }} />
          {/* Highlight */}
          <span style={{ fontSize: 7, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>HL</span>
          {HILIGHTS.map((h, i) => (
            <div key={i} onClick={() => setFmt(f => ({ ...f, highlight: h }))} style={{
              width: 14, height: 14, borderRadius: 2, background: h === 'transparent' ? C.b1 : h,
              cursor: 'pointer', border: fmt.highlight === h ? '2px solid #fff' : `1px solid ${C.b2}`, flexShrink: 0,
            }} />
          ))}
        </div>
        {/* Preview */}
        <div style={{ padding: '8px 14px 4px', background: C.raised, borderBottom: `1px solid ${C.b0}` }}>
          <div style={{ fontSize: 7, color: C.t4, letterSpacing: '.1em', marginBottom: 4, fontFamily: 'JetBrains Mono,monospace' }}>PREVIEW</div>
          <div style={{
            fontSize: fmt.size, color: fmt.color, fontWeight: fmt.bold ? 700 : 400,
            fontStyle: fmt.italic ? 'italic' : 'normal', textDecoration: fmt.underline ? 'underline' : 'none',
            textAlign: fmt.align, background: fmt.highlight, lineHeight: 1.7,
            minHeight: 24, padding: '3px 6px', borderRadius: 2,
          }}>{text || <span style={{ opacity: 0.35 }}>Your annotation preview…</span>}</div>
        </div>
        {/* Textarea */}
        <div style={{ padding: '10px 14px' }}>
          <textarea value={text} onChange={e => setText(e.target.value)}
            placeholder="Write your insight, observation, or finding about this chart…"
            style={{ width: '100%', minHeight: 88, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: 10, fontSize: 12, fontFamily: 'inherit', borderRadius: 3, resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.7 }} />
        </div>
        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '0 14px 14px', alignItems: 'center' }}>
          {initial?.text && (
            <button onClick={() => onSave({ text: '', fmt })} style={{ padding: '5px 12px', background: 'none', border: `1px solid ${C.red}44`, color: C.red, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>REMOVE</button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
            <button onClick={onClose} style={{ padding: '6px 16px', background: 'none', border: `1px solid ${C.b1}`, color: C.t3, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>CANCEL</button>
            <button onClick={() => { if (text.trim()) onSave({ text, fmt }); else onClose(); }} style={{ padding: '6px 18px', background: C.cyan, border: 'none', color: C.void, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700 }}>SAVE</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANNOTATION DISPLAY
// ─────────────────────────────────────────────────────────────────────────────
export function AnnotationDisplay({ annotation, onEdit, compact }) {
  if (!annotation?.text) return null;
  const { text, fmt } = annotation;
  return (
    <div style={{ borderTop: `1px solid ${C.b0}`, padding: compact ? '6px 10px' : '9px 13px' }}>
      {!compact && (
        <div style={{ fontSize: 7, color: C.t4, letterSpacing: '.1em', marginBottom: 4, fontFamily: 'JetBrains Mono,monospace', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          ANNOTATION
          {onEdit && <button onClick={onEdit} style={{ background: 'none', border: 'none', color: C.t3, cursor: 'pointer', fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }}>✎ EDIT</button>}
        </div>
      )}
      <div style={{
        fontSize: fmt?.size || 12, color: fmt?.color || C.t2, fontWeight: fmt?.bold ? 700 : 400,
        fontStyle: fmt?.italic ? 'italic' : 'normal', textDecoration: fmt?.underline ? 'underline' : 'none',
        textAlign: fmt?.align || 'left', background: fmt?.highlight || 'transparent',
        lineHeight: 1.7, padding: '3px 5px', borderRadius: 2,
      }}>{text}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PNG DOWNLOAD  (ECharts getDataURL, 2× DPI)
// ─────────────────────────────────────────────────────────────────────────────
export function downloadChartPNG(elId, title) {
  const el = document.getElementById(elId);
  if (!el) { alert('Chart element not found'); return; }
  let inst = null;
  for (const div of el.querySelectorAll('div')) {
    inst = echarts.getInstanceByDom(div);
    if (inst) break;
  }
  if (!inst) { alert('No ECharts chart found — make sure chart is visible'); return; }
  const url = inst.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: C.surface });
  const a = document.createElement('a');
  a.download = `${(title || 'chart').replace(/[^a-z0-9]/gi, '_')}.png`;
  a.href = url; a.click();
}

// ─────────────────────────────────────────────────────────────────────────────
// ICON BUTTON STYLE
// ─────────────────────────────────────────────────────────────────────────────
const IB = (color, active) => ({
  width: 24, height: 24, background: active ? color + '28' : 'none',
  border: `1px solid ${active ? color : C.b1}`, color: active ? color : C.t4,
  borderRadius: 2, cursor: 'pointer', fontSize: 11, display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0,
});

// ─────────────────────────────────────────────────────────────────────────────
// SLICER PANEL
// ─────────────────────────────────────────────────────────────────────────────
function SlicerPanel({ rawRows, types, slicers, setSlicers }) {
  const catCols = useMemo(() =>
    types.filter(t => t.type === 'categorical' && t.uniqueCount >= 2 && t.uniqueCount <= 40).map(t => t.col)
  , [types]);
  const [active, setActive] = useState(catCols[0] || '');
  const vals = useMemo(() =>
    active ? [...new Set(rawRows.map(r => r[active]).filter(Boolean))].sort().slice(0, 40) : []
  , [rawRows, active]);
  const toggle = (col, val) => setSlicers(p => {
    const cur = p[col] || new Set(), n = new Set(cur);
    n.has(val) ? n.delete(val) : n.add(val);
    return { ...p, [col]: n };
  });
  const totalActive = Object.values(slicers).reduce((s, set) => s + set.size, 0);
  return (
    <div style={{ width: 200, flexShrink: 0, background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden', alignSelf: 'flex-start', position: 'sticky', top: 0 }}>
      <div style={{ padding: '8px 12px', background: C.deep, borderBottom: `1px solid ${C.b0}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 9, color: C.cyan, letterSpacing: '.12em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>⊞ APPLY FILTERS</span>
        {totalActive > 0 && <button onClick={() => setSlicers({})} style={{ fontSize: 8, color: C.red, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>CLEAR ({totalActive})</button>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, padding: '7px 8px 3px' }}>
        {catCols.slice(0, 8).map(c => {
          const on = slicers[c]?.size > 0;
          return (
            <button key={c} onClick={() => setActive(c)} style={{
              padding: '2px 7px', fontSize: 8, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace',
              border: `1px solid ${active === c ? C.cyan : on ? C.gold : C.b1}`,
              background: active === c ? C.cyan + '20' : on ? C.gold + '15' : 'transparent',
              color: active === c ? C.cyan : on ? C.gold : C.t4,
            }}>{c.slice(0, 10)}{on ? '●' : ''}</button>
          );
        })}
      </div>
      {active && (
        <div style={{ padding: '4px 8px 10px', maxHeight: 280, overflowY: 'auto' }}>
          <div style={{ fontSize: 8, color: C.t4, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>{active}</div>
          {vals.map(val => {
            const on = slicers[active]?.has(val);
            return (
              <div key={val} onClick={() => toggle(active, val)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 4px', cursor: 'pointer', borderRadius: 2, marginBottom: 1, background: on ? C.cyan + '18' : 'transparent' }}>
                <div style={{ width: 10, height: 10, border: `1.5px solid ${on ? C.cyan : C.b2}`, borderRadius: 2, background: on ? C.cyan : 'transparent', flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: on ? C.cyan : C.t2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 148 }}>{val}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPLORE CARD  (drag-to-reorder via HTML5 drag API)
// ─────────────────────────────────────────────────────────────────────────────
const ExploreCard = React.memo(function ExploreCard({ chart, rows, colorMap, onHide, onPin, isPinned, index, onDragStart, onDragOver, onDrop }) {
  const [annotating, setAnnotating] = useState(false);
  const [annotation, setAnnotation] = useState(null);
  const [over, setOver]             = useState(false);
  const [overrideType, setOverrideType] = useState(null);
  const alts = getAlternates(chart.type);
  const effectiveChart = useMemo(() => {
    if (!overrideType) return chart;
    const eff = { ...chart, type: overrideType };
    if (!eff.xCol && eff.catCol) eff.xCol = eff.catCol;
    if (!eff.yCol && eff.numCol) eff.yCol = eff.numCol;
    if (!eff.catCol && eff.xCol) eff.catCol = eff.xCol;
    if (!eff.numCol && eff.yCol) eff.numCol = eff.yCol;
    if (!eff.col) eff.col = eff.catCol || eff.xCol || eff.dateCol || '';
    return eff;
  }, [chart, overrideType]);
  const col    = chart.col || chart.xCol;
  const accent = colorMap?.[col] || C.cyan;

  return (
    <div
      draggable
      onDragStart={e => { onDragStart(index); e.dataTransfer.effectAllowed = 'move'; }}
      onDragOver={e => { e.preventDefault(); setOver(true); onDragOver(index); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); onDrop(index); }}
      style={{
        background: C.surface, border: `1px solid ${over ? accent : C.b1}`,
        borderTop: `2px solid ${accent}`, borderRadius: 4, overflow: 'hidden',
        transition: 'border-color .15s, transform .1s', transform: over ? 'scale(1.01)' : 'scale(1)',
      }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 10px', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
        <span draggable={false} title="Drag to reorder" style={{ cursor: 'grab', color: C.t4, fontSize: 14, lineHeight: 1, flexShrink: 0, userSelect: 'none' }}>⠿</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: accent, letterSpacing: '.1em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{chart.title}</div>
          {chart.subtitle && <div style={{ fontSize: 8, color: C.t4, marginTop: 1 }}>{chart.subtitle}</div>}
        </div>
        <div style={{ display: 'flex', gap: 3, flexShrink: 0, alignItems: 'center' }}>
          {alts.length > 0 && (
            <select
              value={overrideType || chart.type}
              onChange={e => setOverrideType(e.target.value === chart.type ? null : e.target.value)}
              title="Convert to different chart type"
              style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t3, padding: '2px 4px', fontSize: 8, fontFamily: 'JetBrains Mono,monospace', borderRadius: 2, cursor: 'pointer', outline: 'none', maxWidth: 84 }}
            >
              {[chart.type, ...alts].map(t => (
                <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
              ))}
            </select>
          )}
          <button title="Annotate" onClick={() => setAnnotating(true)} style={IB(C.gold)}>✎</button>
          <button title={isPinned ? '★ Pinned to Report' : '☆ Pin to Report'} onClick={() => onPin(effectiveChart)} style={IB(isPinned ? C.green : C.t3, isPinned)}>{isPinned ? '★' : '☆'}</button>
          <button title="Hide" onClick={() => onHide(chart.id)} style={IB(C.red)}>×</button>
        </div>
      </div>
      {/* Chart */}
      <div style={{ padding: '8px 10px 6px' }}>
        <RenderChart chart={effectiveChart} rows={rows} colorMap={colorMap} />
      </div>
      <AnnotationDisplay annotation={annotation} onEdit={() => setAnnotating(true)} />
      {annotating && <AnnotationModal initial={annotation} onSave={a => { setAnnotation(a.text ? a : null); setAnnotating(false); }} onClose={() => setAnnotating(false)} />}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD CARD  (free-drag on canvas)
// ─────────────────────────────────────────────────────────────────────────────
function DashboardCard({ card, rows, colorMap, onRemove, onUpdatePos, onUpdateAnnotation }) {
  const { chart, pos, annotation } = card;
  const [annotating, setAnnotating] = useState(false);
  const dragging = useRef(false);
  const startRef = useRef({ mx: 0, my: 0, cx: 0, cy: 0 });
  const col    = chart.col || chart.xCol;
  const accent = colorMap?.[col] || C.cyan;
  const elId   = `dc-${card.id}`;

  const onMouseDown = useCallback(e => {
    if (e.button !== 0) return;
    dragging.current = true;
    startRef.current = { mx: e.clientX, my: e.clientY, cx: pos.x, cy: pos.y };
    e.preventDefault();
  }, [pos.x, pos.y]);

  useEffect(() => {
    const onMove = e => {
      if (!dragging.current) return;
      onUpdatePos(card.id, {
        x: Math.max(0, startRef.current.cx + e.clientX - startRef.current.mx),
        y: Math.max(0, startRef.current.cy + e.clientY - startRef.current.my),
      });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [card.id, onUpdatePos]);

  return (
    <div id={elId} style={{
      position: 'absolute', left: pos.x, top: pos.y, width: pos.w || 360,
      background: C.surface, border: `1px solid ${C.b2}`,
      borderTop: `2px solid ${accent}`, borderRadius: 4, overflow: 'hidden',
      boxShadow: '0 6px 24px rgba(0,0,0,.55)', zIndex: pos.z || 1, minWidth: 260,
    }}>
      {/* Drag header */}
      <div onMouseDown={onMouseDown} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: C.deep, borderBottom: `1px solid ${C.b0}`, cursor: 'grab', userSelect: 'none' }}>
        <span style={{ color: C.t4, fontSize: 13, lineHeight: 1 }}>⠿</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: accent, letterSpacing: '.1em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{chart.title}</div>
          {chart.subtitle && <div style={{ fontSize: 8, color: C.t4 }}>{chart.subtitle}</div>}
        </div>
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          <button title="Annotate" onClick={() => setAnnotating(true)} style={IB(C.gold, !!annotation?.text)}>✎</button>
          <button title="Download PNG" onClick={() => downloadChartPNG(elId, chart.title)} style={IB(C.cyan)}>↓</button>
          <button title="Remove from dashboard" onClick={() => onRemove(card.id)} style={IB(C.red)}>×</button>
        </div>
      </div>
      {/* Chart */}
      <div style={{ padding: '8px 10px 4px' }}>
        <RenderChart chart={chart} rows={rows} colorMap={colorMap} />
      </div>
      <AnnotationDisplay annotation={annotation} onEdit={() => setAnnotating(true)} />
      {annotating && <AnnotationModal initial={annotation} onSave={a => { onUpdateAnnotation(card.id, a.text ? a : null); setAnnotating(false); }} onClose={() => setAnnotating(false)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOM BUILDER (self-contained with pin button)
// ─────────────────────────────────────────────────────────────────────────────
function CustomBuilder({ rows, types, onPin }) {
  const numCols = types.filter(t => t.type === 'numeric').map(t => t.col);
  const allCols = types.map(t => t.col);
  const catCols = types.filter(t => t.type === 'categorical').map(t => t.col);
  const [ctype, setCtype] = useState('bar');
  const [xCol,  setXCol]  = useState(catCols[0] || allCols[0] || '');
  const [yCol,  setYCol]  = useState(numCols[0] || '');
  const [sCol,  setSCol]  = useState(numCols[1] || '');
  const [agg,   setAgg]   = useState('mean');
  const [bins,  setBins]  = useState(20);
  const [annotating, setAnnotating] = useState(false);
  const [annotation, setAnnotation] = useState(null);

  const CTYPES = [['bar','▊ BAR'],['hbar','⊞ H.BAR'],['line','↗ LINE'],['area','◭ AREA'],['pie','◔ PIE'],['donut','◎ DONUT'],['scatter','⬡ SCATTER'],['bubble','● BUBBLE'],['hist','▦ HIST'],['radial','◉ RADIAL']];
  const AGG = ['mean','sum','count','min','max','median'];
  const noXY = ['pie','donut','hbar','hist','radial'];

  const aggrData = useMemo(() => {
    if (!xCol || !yCol) return [];
    const g = {};
    rows.forEach(r => {
      const k = String(r[xCol] || '').slice(0, 18) || '(empty)';
      if (!g[k]) g[k] = [];
      const v = parseFloat(r[yCol]);
      if (!isNaN(v)) g[k].push(v);
    });
    return Object.entries(g).map(([name, vals]) => {
      const sv = [...vals].sort((a, b) => a - b);
      const v = agg === 'sum' ? vals.reduce((a, b) => a + b, 0) : agg === 'count' ? vals.length : agg === 'min' ? sv[0] : agg === 'max' ? sv[sv.length - 1] : agg === 'median' ? sv[Math.floor(sv.length / 2)] : vals.reduce((a, b) => a + b, 0) / vals.length;
      return { name, value: +v.toFixed(4) };
    }).sort((a, b) => b.value - a.value).slice(0, MAX_BAR_GROUPS);
  }, [rows, xCol, yCol, agg]);

  const fakeChart = { id: `custom_${Date.now()}`, type: ctype, col: xCol, xCol, yCol, sizeCol: sCol, catCol: xCol, numCol: yCol, dateCol: xCol, valueCol: yCol, cols: numCols, agg, title: `${xCol}${yCol && !noXY.includes(ctype) ? ' × ' + yCol : ''}`, subtitle: `custom · ${ctype}` };
  const H = 260;

  const renderCustom = () => {
    if (['pie','donut'].includes(ctype)) return <DonutChart rows={rows} col={xCol} donut={ctype === 'donut'} />;
    if (ctype === 'hbar')    return <HBarChart rows={rows} col={xCol} />;
    if (ctype === 'hist')    return <HistChart rows={rows} col={xCol} color={C.cyan} bins={bins} />;
    if (ctype === 'radial')  return <RadialChart rows={rows} col={xCol} />;
    if (ctype === 'scatter') return <ScatterChart2 rows={rows} xCol={xCol} yCol={yCol} color={C.cyan} />;
    if (ctype === 'bubble')  return <BubbleChart rows={rows} xCol={xCol} yCol={yCol} sizeCol={sCol} />;
    if (ctype === 'bar') return <EChart height={H} option={{ backgroundColor:'transparent', tooltip:ecTip(), grid:ecGrid(42), xAxis:ecXCat(aggrData.map(d=>d.name),-22), yAxis:ecYVal(), series:[{type:'bar',data:aggrData.map((d,i)=>({value:d.value,itemStyle:{color:PALETTE[i%PALETTE.length]}})),barMaxWidth:32,name:`${agg}(${yCol})`,itemStyle:{borderRadius:[3,3,0,0]}}] }} />;
    if (ctype === 'line') return <EChart height={H} option={{ backgroundColor:'transparent', tooltip:ecTip(), grid:ecGrid(), xAxis:ecXCat(aggrData.map(d=>d.name)), yAxis:ecYVal(), series:[{type:'line',data:aggrData.map(d=>d.value),smooth:true,symbol:'circle',symbolSize:4,lineStyle:{color:C.cyan,width:2},itemStyle:{color:C.cyan}}] }} />;
    if (ctype === 'area') return <EChart height={H} option={{ backgroundColor:'transparent', tooltip:ecTip(), grid:ecGrid(), xAxis:ecXCat(aggrData.map(d=>d.name)), yAxis:ecYVal(), series:[{type:'line',data:aggrData.map(d=>d.value),smooth:true,areaStyle:{color:C.violet+'44'},lineStyle:{color:C.violet,width:2},itemStyle:{color:C.violet},symbol:'none'}] }} />;
    return <NoData />;
  };

  const selStyle = { background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '5px 8px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, cursor: 'pointer', outline: 'none' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: '12px 14px' }}>
        <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.1em', marginBottom: 6, fontFamily: 'JetBrains Mono,monospace' }}>CHART TYPE</div>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 10 }}>
          {CTYPES.map(([id, l]) => (
            <button key={id} onClick={() => setCtype(id)} style={{ padding: '4px 10px', border: `1px solid ${ctype === id ? C.green : C.b1}`, background: ctype === id ? C.green + '22' : 'transparent', color: ctype === id ? C.green : C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 2, cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>{noXY.includes(ctype) ? 'COLUMN' : 'X / GROUP'}</div><select value={xCol} onChange={e => setXCol(e.target.value)} style={selStyle}>{allCols.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          {!noXY.includes(ctype) && <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>Y / VALUE</div><select value={yCol} onChange={e => setYCol(e.target.value)} style={selStyle}>{numCols.map(c => <option key={c} value={c}>{c}</option>)}</select></div>}
          {ctype === 'bubble' && <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>SIZE</div><select value={sCol} onChange={e => setSCol(e.target.value)} style={selStyle}>{numCols.map(c => <option key={c} value={c}>{c}</option>)}</select></div>}
          {['bar','line','area'].includes(ctype) && <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>AGG</div><select value={agg} onChange={e => setAgg(e.target.value)} style={selStyle}>{AGG.map(a => <option key={a} value={a}>{a}</option>)}</select></div>}
          {ctype === 'hist' && <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>BINS — {bins}</div><input type="range" min={5} max={60} value={bins} onChange={e => setBins(+e.target.value)} style={{ accentColor: C.cyan, width: 100 }} /></div>}
        </div>
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '6px 10px', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
          <button onClick={() => setAnnotating(true)} style={{ ...IB(C.gold), width: 'auto', padding: '3px 10px', fontSize: 9, fontFamily: 'JetBrains Mono,monospace', gap: 5 }}>✎ ANNOTATE</button>
          <button onClick={() => onPin({ ...fakeChart, id: `custom_${Date.now()}` })} style={{ ...IB(C.green), width: 'auto', padding: '3px 10px', fontSize: 9, fontFamily: 'JetBrains Mono,monospace', gap: 5, fontWeight: 700 }}>★ PIN TO REPORT</button>
        </div>
        <div style={{ padding: 14 }}>{renderCustom()}</div>
        <AnnotationDisplay annotation={annotation} onEdit={() => setAnnotating(true)} />
        {annotating && <AnnotationModal initial={annotation} onSave={a => { setAnnotation(a.text ? a : null); setAnnotating(false); }} onClose={() => setAnnotating(false)} />}
      </div>

      {/* ── Data preview table ────────────────────────────────────────── */}
      <CustomDataPreview rows={rows} types={types} />
    </div>
  );
}

function CustomDataPreview({ rows, types }) {
  const MODES = ['head', 'tail', 'sample', 'slice'];
  const [mode, setMode] = useState('head');
  const [sliceFrom, setSliceFrom] = useState(0);
  const [sliceTo,   setSliceTo]   = useState(Math.min(50, rows.length));

  const headers = types.map(t => t.col);

  const previewRows = useMemo(() => {
    if (mode === 'head')   return rows.slice(0, 50);
    if (mode === 'tail')   return rows.slice(-50);
    if (mode === 'slice')  return rows.slice(Math.max(0, sliceFrom), Math.min(rows.length, sliceTo));
    // sample
    const copy = [...rows];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, 50);
  }, [rows, mode, sliceFrom, sliceTo]);

  const btnStyle = (active) => ({
    padding: '5px 13px', border: `1px solid ${active ? C.cyan : C.b1}`,
    background: active ? C.cyan + '22' : 'transparent',
    color: active ? C.cyan : C.t3,
    fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700,
    borderRadius: 3, cursor: 'pointer',
  });

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: C.deep, borderBottom: `1px solid ${C.b0}`, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.08em', marginRight: 4 }}>DATA PREVIEW</span>
        
        {mode === 'slice' && (
          <>
            <input type="number" min={0} max={rows.length - 1} value={sliceFrom}
              onChange={e => setSliceFrom(Math.max(0, parseInt(e.target.value) || 0))}
              style={{ width: 72, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '4px 6px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none' }}
              placeholder="from" />
            <span style={{ color: C.t4, fontSize: 10 }}>–</span>
            <input type="number" min={1} max={rows.length} value={sliceTo}
              onChange={e => setSliceTo(Math.min(rows.length, Math.max(1, parseInt(e.target.value) || 1)))}
              style={{ width: 72, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '4px 6px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none' }}
              placeholder="to" />
          </>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{previewRows.length} / {rows.length} rows</span>
      </div>
      <DataTable rows={previewRows} headers={headers} types={types} showControls />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function DashboardEngine({ ds, onDashboardUpdate, initialCards, enableCrossAnalysis = false }) {
  const { rows: rawRows, headers } = ds;
  const [activeTab,   setActiveTab]   = useState('auto');
  const [slicers,     setSlicers]     = useState({});
  const [hidden,      setHidden]      = useState(new Set());
  const [showSlicers, setShowSlicers] = useState(false);
  const [pinnedCards, setPinnedCards] = useState(initialCards || []);
  const [chartOrder,  setChartOrder]  = useState(null);
  const dragSrc = useRef(null);

  const { rows: sampRows, sampled, orig } = useMemo(() => smartSample(rawRows), [rawRows]);
  const filtRows = useMemo(() => {
    let r = sampRows;
    Object.entries(slicers).forEach(([col, vals]) => { if (vals.size > 0) r = r.filter(row => vals.has(row[col])); });
    return r;
  }, [sampRows, slicers]);

  const types    = useMemo(() => inferTypes(headers, filtRows), [headers, filtRows]);
  const colorMap = useMemo(() => { const m = {}; headers.forEach((h, i) => { m[h] = PALETTE[i % PALETTE.length]; }); return m; }, [headers]);
  const autoList = useMemo(() => buildAutoCharts(filtRows, headers, types), [filtRows, headers, types]);

  const ordered = useMemo(() => {
    if (!chartOrder) return autoList;
    const map = Object.fromEntries(autoList.map(c => [c.id, c]));
    return [...chartOrder.map(id => map[id]).filter(Boolean), ...autoList.filter(c => !chartOrder.includes(c.id))];
  }, [autoList, chartOrder]);

  const visible    = ordered.filter(c => !hidden.has(c.id));
  const hiddenList = ordered.filter(c => hidden.has(c.id));
  const totalFilters = Object.values(slicers).reduce((s, set) => s + set.size, 0);

  const pinChart = useCallback(chart => {
    setPinnedCards(prev => {
      if (prev.some(c => c.chart.id === chart.id)) return prev;
      const idx = prev.length;
      const x = 20 + (idx % 3) * 390, y = 20 + Math.floor(idx / 3) * 430;
      const next = [...prev, { id: `pin_${Date.now()}_${chart.id}`, chart, pos: { x, y, w: 360 }, annotation: null }];
      onDashboardUpdate?.(next);
      return next;
    });
  }, [onDashboardUpdate]);

  const removeFromDash = useCallback(id => {
    setPinnedCards(prev => { const next = prev.filter(c => c.id !== id); onDashboardUpdate?.(next); return next; });
  }, [onDashboardUpdate]);

  const updatePos = useCallback((id, newPos) => {
    setPinnedCards(prev => prev.map(c => c.id === id ? { ...c, pos: { ...c.pos, ...newPos } } : c));
  }, []);

  const updateAnnotation = useCallback((id, ann) => {
    setPinnedCards(prev => {
      const next = prev.map(c => c.id === id ? { ...c, annotation: ann } : c);
      onDashboardUpdate?.(next);
      return next;
    });
  }, [onDashboardUpdate]);

  const isPinned = useCallback(chart => pinnedCards.some(c => c.chart.id === chart.id), [pinnedCards]);

  const visibleRef = useRef(visible);
  useEffect(() => { visibleRef.current = visible; }, [visible]);

  const handleDragStart = useCallback(i => { dragSrc.current = i; }, []);
  const handleDragOver  = useCallback(() => {}, []);
  const handleDrop      = useCallback(i => {
    if (dragSrc.current == null || dragSrc.current === i) return;
    const arr = [...visibleRef.current];
    const [moved] = arr.splice(dragSrc.current, 1);
    arr.splice(i, 0, moved);
    setChartOrder(arr.map(c => c.id));
    dragSrc.current = null;
  }, []);
  const handleHide = useCallback(id => setHidden(s => new Set([...s, id])), []);

  const canvasMinH = Math.max(500, ...pinnedCards.map(c => c.pos.y + 440));
  const tabDefs = [
    ['auto', '◈ AUTO', C.cyan],
    ['dashboard', '★ DASHBOARD', C.gold],
    ['custom', '⬢ CUSTOM', C.green],
    ...(enableCrossAnalysis ? [['cross', '✳ CROSS', C.violet]] : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* ── Tab / control bar ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, background: C.deep, border: `1px solid ${C.b0}`, borderRadius: 4, padding: 3 }}>
          {tabDefs.map(([id, l, c]) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{
              padding: '6px 14px', border: 'none', borderRadius: 3, cursor: 'pointer',
              background: activeTab === id ? c : 'transparent', color: activeTab === id ? C.void : C.t2,
              fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 700, letterSpacing: '.09em',
            }}>{l}</button>
          ))}
        </div>

        {sampled && <span style={{ fontSize: 8, color: C.gold, background: C.gold+'15', border:`1px solid ${C.gold}44`, padding:'2px 6px', borderRadius:2, fontFamily:'JetBrains Mono,monospace' }}>⚡ {sampRows.length.toLocaleString()}/{orig.toLocaleString()} rows</span>}
        {totalFilters > 0 && <span style={{ fontSize: 8, color: C.gold, background: C.gold+'15', border:`1px solid ${C.gold}44`, padding:'2px 6px', borderRadius:2, fontFamily:'JetBrains Mono,monospace' }}>🔲 {totalFilters} filter{totalFilters!==1?'s':''} · {filtRows.length.toLocaleString()} rows</span>}
        {pinnedCards.length > 0 && <span style={{ fontSize: 8, color: C.green, background: C.green+'15', border:`1px solid ${C.green}44`, padding:'2px 6px', borderRadius:2, fontFamily:'JetBrains Mono,monospace' }}>★ {pinnedCards.length} on dashboard</span>}

        {(activeTab === 'auto' || activeTab === 'custom') && (
          <button onClick={() => setShowSlicers(s => !s)} style={{ marginLeft: 'auto', padding: '5px 11px', border: `1px solid ${showSlicers ? C.cyan : C.b1}`, background: showSlicers ? C.cyan+'18' : 'transparent', color: showSlicers ? C.cyan : C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 3, cursor: 'pointer' }}>⊞ APPLY FILTERS{totalFilters>0?` (${totalFilters})`:''}</button>
        )}
      </div>

      {/* ── EXPLORE ─────────────────────────────────────────────────── */}
      {activeTab === 'auto' && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {showSlicers && <SlicerPanel rawRows={rawRows} types={types} slicers={slicers} setSlicers={setSlicers} />}
          <div style={{ flex: 1, minWidth: 0 }}>
                {hiddenList.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', padding: '7px 12px', background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, marginBottom: 12 }}>
                    <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>HIDDEN:</span>
                    {hiddenList.map(c => (
                      <button key={c.id} onClick={() => setHidden(s => { const n = new Set(s); n.delete(c.id); return n; })} style={{ padding: '2px 8px', border: `1px solid ${C.b1}`, background: 'transparent', color: C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: 'pointer' }}>+ {c.title.slice(0, 18)}</button>
                    ))}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 14 }}>
                  {visible.map((chart, idx) => (
                    <ExploreCard key={chart.id} chart={chart} rows={filtRows} colorMap={colorMap}
                      onHide={handleHide}
                      onPin={pinChart} isPinned={isPinned(chart)}
                      index={idx} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} />
                  ))}
                  {visible.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: C.t4, gridColumn: '1/-1' }}>All charts hidden — restore above</div>}
                </div>
          </div>
        </div>
      )}

      {/* ── DASHBOARD CANVAS ──────────────────────────────────────────── */}
      {activeTab === 'custom' && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {showSlicers && <SlicerPanel rawRows={rawRows} types={types} slicers={slicers} setSlicers={setSlicers} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <CustomBuilder rows={filtRows} types={types} onPin={pinChart} />
          </div>
        </div>
      )}

      {/* ── DASHBOARD CANVAS ─────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        pinnedCards.length === 0 ? (
          <div style={{ height: 380, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.t4, border: `2px dashed ${C.b1}`, borderRadius: 6, background: C.raised }}>
            <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.25 }}>★</div>
            <div style={{ fontSize: 14, color: C.t3, marginBottom: 4 }}>Dashboard is empty</div>
            <div style={{ fontSize: 11 }}>Pin charts from Auto or Custom using the ☆ button</div>
          </div>
        ) : (
          <div style={{ position: 'relative', minHeight: canvasMinH, background: `radial-gradient(circle at 1px 1px, ${C.b0} 1px, transparent 0) 0 0 / 28px 28px`, borderRadius: 4, border: `1px solid ${C.b1}`, overflow: 'hidden' }}>
            {pinnedCards.map(card => (
              <DashboardCard key={card.id} card={card} rows={filtRows} colorMap={colorMap}
                onRemove={removeFromDash} onUpdatePos={updatePos} onUpdateAnnotation={updateAnnotation} />
            ))}
          </div>
        )
      )}

      {activeTab === 'cross' && (
        <CrossAnalysis rows={filtRows} types={types} />
      )}
    </div>
  );
}
